import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoInstalacion, TipoInstalacionPagingRes } from '@/shared/interfaces';
import { getUrlParamsFetch } from '@/shared/utils';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTipoInstalacion = (
  params?: GetTiposInstalacionParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['tiposInstalacion', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getTiposInstalacion(params);
    },
    enabled,
  });
};

export const useGetTipoInstalacion = (id: string) => {
  return useQuery({
    queryKey: ['tiposInstalacion', id],
    queryFn: async () => {
      return getTipoInstalacion(id);
    },
  });
};

export const useCreateTipoInstalacion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoInstalacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposInstalacion'] });
    },
    onSettled: () => {
      navigate(returnUrl, { replace: true });
    },
  });
};

export const useUpdateTipoInstalacion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoInstalacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposInstalacion'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetTiposInstalacionParams = Partial<TipoInstalacion> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateTipoInstalacionParams = Omit<
  TipoInstalacion,
  'id_tipo_instalacion'
>;
export type UpdateTipoInstalacionParams = {
  id: string;
  data: CreateTipoInstalacionParams;
};

export const getTiposInstalacion = async (
  params?: GetTiposInstalacionParams
) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TipoInstalacionPagingRes>(
    `${urlBackend}api/v1/instalacion/?${queryParams}`,
    true
  );
};

export const getTipoInstalacion = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<TipoInstalacion>(
    `${urlBackend}api/v1/instalacion/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createTipoInstalacion = async (
  data: CreateTipoInstalacionParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoInstalacion>(
    `${urlBackend}api/v1/instalacion/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoInstalacion = async ({
  data,
  id,
}: UpdateTipoInstalacionParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoInstalacion>(
    `${urlBackend}api/v1/instalacion/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
